import { Tag } from 'lucide-react';
import { AGENT_LABEL_NAME_MAX_LENGTH } from '@reflex/shared';
import {
  useAgentLabels,
  useApplyAgentLabel,
  useCreateAgentLabel,
} from '@/hooks/agents/useAgentLabels';
import { TaxonomyPickerDialog } from '@/components/agent/TaxonomyPickerDialog';

export interface AddLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The agents to label. Usually one row; the selection bar passes many. */
  agentIds: string[];
  /** Fired after a successful apply, so a caller can leave select mode. */
  onAdded?: () => void;
}

/**
 * Applies a label by hand, or extends the vocabulary with one the classifier
 * has never had. Same control as the group picker — the manual path through
 * both is identical — wired to the label catalog instead.
 *
 * The list is rarely empty here: an organization's first read seeds the
 * built-in vocabulary, so this usually opens on a set of labels to correct
 * the classifier with rather than a blank slate.
 */
export function AddLabelDialog({ open, onOpenChange, agentIds, onAdded }: AddLabelDialogProps) {
  const { data: labels = [] } = useAgentLabels();
  const applyLabel = useApplyAgentLabel();
  const createLabel = useCreateAgentLabel();

  const finish = () => {
    onOpenChange(false);
    onAdded?.();
  };

  return (
    <TaxonomyPickerDialog
      open={open}
      onOpenChange={onOpenChange}
      agentCount={agentIds.length}
      title="Add label"
      description="Choose a label, or type a new name to create one."
      icon={<Tag className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
      items={labels}
      pending={applyLabel.isPending || createLabel.isPending}
      maxNameLength={AGENT_LABEL_NAME_MAX_LENGTH}
      emptyHint="Type a name to create your first label."
      searchPlaceholder="Search or name a new label…"
      searchLabel="Search or name a new label"
      testIdPrefix="add-label"
      onSelect={(labelId) => {
        if (agentIds.length === 0) return;
        applyLabel.mutate({ labelId, agentIds }, { onSuccess: finish });
      }}
      onCreate={(name) => createLabel.mutate({ name, agentIds }, { onSuccess: finish })}
    />
  );
}
